import React, { useContext, useState, useEffect } from "react";
import { ThemeContext } from "../../lib/main-context";
import { useTranslation } from "../../i18n/TranslationProvider";
import moment from "moment";
import "moment/locale/es";
import "moment/locale/zh-cn";
import "moment/locale/id";

import { Line } from "react-chartjs-2";
import { parseISO, subDays } from "date-fns";

import theme from "../../index.scss";
import SubmitButton from "../UIComponents/SubmitButton";
import ModalPdf from "./ModalPdf";
import options from "./LineChartOptions";

import fillInDates from "../../lib/fillInDates";
import triggerWithSetVal from "../../lib/triggerWithSetVal";

const LineChart = ({ data, additionalNotes }) => {
  const [show, setShow] = useState(false);
  const { dark } = useContext(ThemeContext);
  const { t, language } = useTranslation();

  useEffect(() => {
    const localeMap = { es: "es", "zh-Hans": "zh-cn", id: "id", en: "en" };
    moment.locale(localeMap[language] || "en");
  }, [language]);

  const tSymptom = (factor) => {
    const categories = ["cognitive", "sleep", "emotional", "physical", "speech", "vision",
      "wellness", "travel", "screen_time", "environmental", "dietary", "situational", "other"];
    const safe = (factor || "").trim().toLowerCase()
      .replace(/ /g,"_").replace(/\//g,"_").replace(/,/g,"")
      .replace(/:/g,"").replace(/'/g,"").replace(/[()]/g,"").replace(/-/g,"_")
      .substring(0, 60);
    for (const cat of categories) {
      // try symptom namespace
      const symKey = `symptom.${cat}.${safe}`;
      const symTranslated = t(symKey);
      if (symTranslated !== symKey) return symTranslated;
      // try trigger namespace
      const trigKey = `trigger.${cat}.${safe}`;
      const trigTranslated = t(trigKey);
      if (trigTranslated !== trigKey) return trigTranslated;
    }
    return factor;
  };

  const colors = [
    theme.chart1, theme.chart2, theme.chart3, theme.chart4, theme.chart5,
    theme.chart6, theme.chart7, theme.chart8, theme.chart9, theme.chart10,
  ];
  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  let datasetArr = [];
  const startDate = subDays(new Date(), 7);
  const recentMedical = data
    .filter((item) => item.category === "medical")
    .filter((item) => {
      const d = parseISO(item.symptom_date);
      return d >= startDate;
    });

  const factors = Array.from(new Set(recentMedical.map((item) => item.factor)));

  factors.forEach((factor) => {
    const rows = recentMedical.filter((item) => item.factor === factor);
    if (rows.length > 0) datasetArr.push(rows);
  });

  let triggerList = triggerWithSetVal(data);
  datasetArr.push(triggerList);

  const mapData = (dataset) =>
    dataset.map((item) => ({
      x: item.symptom_date.slice(0, 10),
      y: item.severity,
      desc: item.description,
    }));

  const newData = { datasets: [] };
  let { datasets } = newData;

  datasetArr.forEach((dataset, index) => {
    if (index < datasetArr.length - 1) {
      datasets.push({
        fill: false,
        label: tSymptom(factors[index]),
        backgroundColor: colors[index],
        borderColor: colors[index],
        borderWidth: 1,
        hoverBackgroundColor: colors[index],
        hoverBorderColor: "white",
        pointBorderWidth: 4,
        data: mapData(dataset),
      });
    } else {
      datasets.push({
        label: t("expandedchart.trigger"),
        data: mapData(dataset),
        pointStyle: "triangle",
        type: "scatter",
        backgroundColor: theme.primary,
        borderWidth: 1,
      });
    }
  });

  datasets = fillInDates(datasets, 7);
  newData.notes = additionalNotes;

  options.scales.xAxes[0].ticks = {
    fontColor: dark ? theme.darkModeText : "#666",
  };
  options.scales.yAxes[0].ticks = {
    beginAtZero: true,
    fontColor: dark ? theme.darkModeText : "#666",
  };

  return (
    <>
      <div className="lineChart">
        <Line
          data={newData}
          width={100}
          height={300}
          options={{
            ...options,
            maintainAspectRatio: false,
            title: {
              display: true,
              text: t("linechart.week_in_review"),
              fontFamily: "quicksand",
              fontStyle: "bold",
              fontColor: dark ? theme.darkModeText : "#666",
            },
            legend: {
              labels: {
                fontColor: dark ? theme.darkModeText : "#666",
                usePointStyle: true,
                boxWidth: 8,
              },
            },
          }}
        />
      </div>
      <SubmitButton
        onClick={() => {
          if (dark) {
            alert(t("common.off_dark_mode_export"));
          } else {
            handleShow();
          }
        }}
      >
        {t("common.export_pdf")}
      </SubmitButton>
      <ModalPdf
        show={show}
        handleClose={handleClose}
        chart="lineChart"
        data={data}
        additionalNotes={additionalNotes}
        days={7}
      />
    </>
  );
};

export default LineChart;